import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import TopicCard from "@/components/base/TopicCard";
import EventTimeline from "./components/EventTimeline";
import SentimentMeter from "./components/SentimentMeter";
import FactCheckList from "./components/FactCheckList";
import { getTopicById, topics } from "@/mocks/topics";
import { siteInfo } from "@/mocks/site";
import { formatHeat, getCategoryName, statusMeta, verdictMeta } from "@/utils/news";

const sections = [
  { id: "overview", label: "事件概览" },
  { id: "timeline", label: "关键时刻" },
  { id: "disputes", label: "争议焦点" },
  { id: "sentiment", label: "评论风向" },
  { id: "responses", label: "当事方回应" },
  { id: "factcheck", label: "辟谣核查" },
  { id: "related", label: "相关事件" },
];

export default function TopicPage() {
  const { id = "" } = useParams();
  const topic = getTopicById(id);

  if (!topic) {
    return (
      <section className="flex flex-col items-center px-4 py-24 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-file-search-line text-2xl" aria-hidden="true"></i>
        </span>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">没有找到这个事件</h1>
        <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
          它可能已经被合并到其他事件，或链接有误。你可以回到首页看看正在追踪的热点。
        </p>
        <Link
          to="/"
          className="mt-6 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </section>
    );
  }

  const status = statusMeta(topic.status);
  const categoryName = getCategoryName(topic.category);
  const related = topic.related
    .map((relId) => topics.find((item) => item.id === relId))
    .filter((item): item is NonNullable<typeof item> => Boolean(item));

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: topic.title,
      description: topic.summary,
      inLanguage: "zh-CN",
      datePublished: topic.publishedAt,
      dateModified: topic.updatedAt,
      articleSection: categoryName,
      keywords: topic.tags.join(","),
      author: { "@type": "Organization", name: siteInfo.name },
      publisher: { "@type": "Organization", name: siteInfo.name },
      mainEntityOfPage: `https://dfjor.cn/topic/${topic.id}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://dfjor.cn/" },
        {
          "@type": "ListItem",
          position: 2,
          name: categoryName,
          item: `https://dfjor.cn/category/${topic.category}`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: topic.title,
          item: `https://dfjor.cn/topic/${topic.id}`,
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: topic.factChecks.map((check) => ({
        "@type": "Question",
        name: check.claim,
        acceptedAnswer: {
          "@type": "Answer",
          text: `核查结论：${verdictMeta(check.verdict).label}。${check.detail}`,
        },
      })),
    },
  ];

  return (
    <>
      <Seo
        title={`${topic.title} - 蝌蚪爆料 | ${categoryName}全程追踪`}
        description={`${topic.summary.slice(0, 70)} 蝌蚪爆料梳理该事件的${topic.timeline.length}个关键节点、争议焦点与当事方回应。`}
        keywords={`${topic.tags.join(",")},蝌蚪爆料,${categoryName},事件追踪`}
        path={`/topic/${topic.id}`}
        ogType="article"
        jsonLd={structuredData}
      />

      <section className="border-b border-background-200 bg-background-100 px-4 py-8 md:px-6 md:py-10 lg:px-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: categoryName, to: `/category/${topic.category}` },
            { label: topic.title },
          ]}
        />

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${status.className}`}>
            <i className={`${status.icon} text-sm`} aria-hidden="true"></i>
            {status.label}
          </span>
          <Link
            to={`/category/${topic.category}`}
            className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-800 transition-colors hover:bg-secondary-200"
          >
            <i className="ri-bookmark-line text-sm" aria-hidden="true"></i>
            {categoryName}
          </Link>
        </div>

        <h1 className="mt-4 max-w-4xl font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-4xl">
          {topic.title}
        </h1>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-foreground-600">{topic.tagline}</p>

        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-fire-line text-sm text-primary-500" aria-hidden="true"></i>
            热度 {formatHeat(topic.heat)}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-calendar-line text-sm" aria-hidden="true"></i>
            首发 {topic.publishedAt}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-refresh-line text-sm" aria-hidden="true"></i>
            最近更新 {topic.updatedAt}
          </span>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {topic.tags.map((tag) => (
            <span key={tag} className="rounded-full border border-background-300 px-3 py-1 text-xs text-foreground-600">
              # {tag}
            </span>
          ))}
        </div>
      </section>

      <section className="px-4 pb-2 pt-6 md:px-6 lg:px-8">
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="h-56 w-full overflow-hidden bg-background-100 md:h-80 lg:h-96">
            <img
              src={topic.cover}
              alt={`${topic.title} 事件配图`}
              title={`${topic.title} - 蝌蚪爆料`}
              className="h-full w-full object-top"
            />
          </div>
        </div>
      </section>

      <div className="px-4 py-10 md:px-6 md:py-12 lg:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
          <div className="min-w-0">
            <article id="overview" className="scroll-mt-24">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">事件概览</h2>
              <p className="mt-5 rounded-lg border-l-4 border-primary-400 bg-primary-50 px-5 py-4 text-sm leading-relaxed text-foreground-800">
                {topic.summary}
              </p>
              <div className="mt-5 space-y-4">
                {topic.overview.map((paragraph) => (
                  <p key={paragraph.slice(0, 12)} className="text-sm leading-7 text-foreground-700">
                    {paragraph}
                  </p>
                ))}
              </div>
            </article>

            <section id="timeline" className="mt-12 scroll-mt-24">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">关键时刻</h2>
              <p className="mt-3 text-sm text-foreground-600">
                按发生顺序排列，最新的节点在最下方。
              </p>
              <EventTimeline items={topic.timeline} />
            </section>

            <section id="disputes" className="mt-12 scroll-mt-24">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">争议焦点</h2>
              <p className="mt-3 text-sm text-foreground-600">把情绪放一边，这几条才是双方真正在争的地方。</p>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {topic.disputes.map((item, index) => (
                  <div key={item.point} className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 font-heading text-sm font-bold text-primary-700">
                      {index + 1}
                    </span>
                    <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">{item.point}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.detail}</p>
                  </div>
                ))}
              </div>
            </section>

            <section id="sentiment" className="mt-12 scroll-mt-24">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">评论风向</h2>
              <p className="mt-3 text-sm text-foreground-600">大致看看讨论区的声音分布，别被最激烈的那几条带偏。</p>
              <SentimentMeter sentiment={topic.sentiment} />
            </section>

            <section id="responses" className="mt-12 scroll-mt-24">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">当事方回应</h2>
              <p className="mt-3 text-sm text-foreground-600">以公开可见的声明与说明为准，未回应的会如实标注。</p>
              <div className="mt-6 space-y-4">
                {topic.responses.map((item) => (
                  <div key={`${item.party}-${item.time}`} className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
                        <i className="ri-user-voice-line text-base text-secondary-600" aria-hidden="true"></i>
                        {item.party}
                      </span>
                      <time className="text-xs text-foreground-500">{item.time}</time>
                    </div>
                    <p className="mt-3 border-l-2 border-secondary-300 pl-4 text-sm leading-relaxed text-foreground-700">
                      {item.content}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section id="factcheck" className="mt-12 scroll-mt-24">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">辟谣核查</h2>
              <p className="mt-3 text-sm text-foreground-600">
                针对流传较广的说法逐条核查，给出四档结论。
              </p>
              <FactCheckList items={topic.factChecks} />
            </section>

            <section id="related" className="mt-12 scroll-mt-24">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关事件</h2>
              <p className="mt-3 text-sm text-foreground-600">主题相近或同一类型的热点，一起看更容易看清规律。</p>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((item) => (
                  <TopicCard key={item.id} topic={item} />
                ))}
              </div>
            </section>
          </div>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">事件速览</h2>
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-600">当前状态</dt>
                  <dd className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${status.className}`}>
                    <i className={`${status.icon} text-xs`} aria-hidden="true"></i>
                    {status.label}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-600">讨论热度</dt>
                  <dd className="font-heading font-semibold text-foreground-950">{formatHeat(topic.heat)}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-600">关键节点</dt>
                  <dd className="font-heading font-semibold text-foreground-950">{topic.timeline.length} 个</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-600">核查结论</dt>
                  <dd className="font-heading font-semibold text-foreground-950">{topic.factChecks.length} 条</dd>
                </div>
              </dl>
            </div>

            <nav className="mt-4 rounded-lg border border-background-200 bg-background-100 p-5" aria-label="本页目录">
              <h2 className="font-heading text-base font-bold text-foreground-950">本页目录</h2>
              <ul className="mt-3 space-y-1">
                {sections.map((section) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground-600 transition-colors hover:bg-background-200 hover:text-foreground-950"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-primary-400"></span>
                      {section.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="mt-4 rounded-lg border border-accent-300 bg-accent-50 p-5">
              <h2 className="font-heading text-base font-bold text-accent-900">发现新进展？</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                如果你手上有可公开核实的材料，欢迎发给我们，核实后会更新到时间线上。
              </p>
              <Link
                to="/contact"
                className="mt-4 flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-megaphone-line text-base" aria-hidden="true"></i>
                提供线索
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}